import api from "@/api";

type Iuser = {
  nickname?: string;
  name?: string;
  email?: string;
  last_name?: string;
  id?: number;
  cupon_share?: string;
  level?: number;
};

export const userStore = defineStore("user", {
  state: () => ({
    user: {} as Iuser,
    isLoggedIn: false as boolean,
    isActiveUser: false as boolean,
    newUser: true as boolean,
    // Plan type of the active order ("Gold" | "Plus" | "Basic"), fed by
    // fetchPlanType(). Persisted so the Gold theme of /minhaconta is already
    // correct on the first paint of the next visit (no white → black flip).
    planTypeName: null as string | null,
    // PlanType id (1 = Basic, 2 = Plus, 3 = Gold) of the entitlement that makes
    // isActiveUser true — same Order, same rule, fed by getActiveUser() on every
    // boot. Kept apart from planTypeName, which comes from isOrderActiveUser and
    // applies the stricter "order_status = ACTIVE" rule (it would report null
    // for a subscriber who cancelled but is still inside the paid period).
    planTypeId: null as number | null,
  }),
  getters: {
    isAdminUser: (state): boolean =>
      state.user?.level === 1 || state.user?.level === 4,
    // Same rule as the Área Gold gate (pages/[language]/area-gold/index.vue):
    // admin (level 1/4) is treated as Gold so the team can preview it.
    isGoldUser(state): boolean {
      return state.planTypeName === "Gold" || this.isAdminUser;
    },
  },
  actions: {
    async setNewUser(payload: boolean) {
      this.newUser = payload;
    },
    async setUser(payload: object) {
      this.user = payload;
      await this.setIsLoggedIn(true);
    },
    async setIsLoggedIn(payload: boolean) {
      this.isLoggedIn = payload;
    },
    async setIsActiveUser(payload: boolean) {
      this.isActiveUser = payload;
    },
    async getActiveUser() {
      try {
        const { data } = await api.get("auth/isActiveUser");
        this.setIsActiveUser(Boolean(data?.active));
        this.planTypeId =
          data?.plan_type_id != null ? Number(data.plan_type_id) : null;
      } catch (error: any) {
        // 401s never reach here: the axios interceptor either refreshed and
        // retried, or it called jwtLogout (which clears isActiveUser via
        // userLogout). Anything else (network blip, 5xx) is treated as a
        // hint, not authority — preserve the persisted flag instead of
        // downgrading to false. Hard server-side errors still log out.
        if (error?.response?.status === 500) {
          await this.userLogout();
        }
      }
      return this.isActiveUser;
    },
    // Active order of the user. Besides feeding planTypeName (used by the Gold
    // theme), it returns orderActive for the callers that need the whole order
    // (plan cards, expiration date, upgrade balance).
    async fetchPlanType(locale: string = "") {
      try {
        const { data } = await api.get(
          `auth/isOrderActiveUser?locale=${locale}`
        );
        const orderActive = data?.orderActive;
        this.planTypeName = orderActive?.plan_type_name ?? null;
        return orderActive;
      } catch (error: any) {
        // 401 = session gone (the interceptor already handles the logout).
        // Anything else is a hint, not authority: keep the persisted value
        // instead of flipping the theme because of a network blip.
        if (error?.response?.status === 401) this.planTypeName = null;
        return null;
      }
    },
    async userLogout() {
      await this.setUser({});
      await this.setIsLoggedIn(false);
      await this.setIsActiveUser(false);
      this.planTypeName = null;
      this.planTypeId = null;
      localStorage.removeItem("user");
    },
  },
  persist: {
    key: "user",
    storage: piniaPluginPersistedstate.localStorage(),
  },
});
